import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  TextField,
  Slider,
  FormControl,
  Select,
  MenuItem,
  Chip,
  FormLabel,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  XIcon,
  CheckIcon,
  TrashIcon,
  CaretDownIcon,
  CaretRightIcon,
  PlayIcon,
  PauseIcon,
  CopyIcon,
} from '@phosphor-icons/react';
import { Node } from '@xyflow/react';
import { StageData } from '../../../utils/redux/slices/callStagesFlowSlice';
import { commonStyles } from '../../../utils/styles/inputs';
import { useQuery } from '@tanstack/react-query';
import { getAllVoices, getAgentToolsApi } from '../../../utils/apis';
import theme from '../../../theme';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import useCopyToClipboard from '../../../hooks/useCopyToClipboard';
import ConfirmationModal from '../../modals/CofirmationModal';
// Add these imports
import { useSelector } from 'react-redux';
import { RootState } from '../../../utils/redux';
import { UltravoxToolData } from '../../../schemas/apis/interface';

interface Voice {
  _id: string;
  voice_id: string;
  name: string;
  language: string;
  preview_url: string;
  labels: Record<string, string>;
}

interface StageConfigPanelProps {
  node: Node<StageData>;
  onClose: () => void;
  onUpdate: (nodeId: string, data: Partial<StageData>) => void;
  onDelete: (nodeId: string) => void;
  readOnly?: boolean;
}

const StageConfigPanel: React.FC<StageConfigPanelProps> = ({
  node,
  onClose,
  onUpdate,
  onDelete,
  readOnly = false,
}) => {
  // Access edges from Redux store
  const { edges } = useSelector((state: RootState) => state.callStagesFlow);

  // Check if current node has incoming edges (target connections)
  const hasIncomingConnections = edges.some(edge => edge.target === node.id);

  const [formData, setFormData] = useState<StageData>({
    id: node?.data?.id,
    stageName: node?.data?.stageName,
    systemPrompt: node?.data?.systemPrompt,
    voice: node?.data?.voice || 'default',
    temperature: node?.data?.temperature || 0.7,
    tools: node?.data?.tools || [],
    languageHint: node?.data?.languageHint || 'en',
    initialMessage:
      node?.data?.initialMessage || 'Hello! How can I help you today?',
  });

  const [originalFormData, setOriginalFormData] = useState<StageData | null>(
    null
  );

  const [isSystemPromptCollapsed, setIsSystemPromptCollapsed] = useState(false);

  // Voice filtering states - service hardcoded to Ultra
  const selectedService = 'Ultra';
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedAccent, setSelectedAccent] = useState('all');
  const [filteredVoices, setFilteredVoices] = useState<Voice[]>([]);

  const [currentlyPlayingVoice, setCurrentlyPlayingVoice] = useState<
    string | null
  >(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(
    null
  );

  // Add state for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  useEffect(() => {
    const initialData = {
      id: node?.data?.id,
      stageName: node?.data?.stageName,
      systemPrompt: node?.data?.systemPrompt,
      voice: node?.data?.voice || 'default',
      temperature: node?.data?.temperature || 0.7,
      tools: node?.data?.tools || [],
      languageHint: node?.data?.languageHint || 'en',
      initialMessage:
        node?.data?.initialMessage || 'Hello! How can I help you today?',
    };
    setFormData(initialData);
    setOriginalFormData(initialData);
  }, [node]);

  // API call to fetch voices - always using Ultra service
  const { data: voiceData, isLoading: isLoadingVoices } = useQuery({
    queryKey: ['get-all-voices', selectedService],
    queryFn: () => getAllVoices({ service: selectedService }),
    enabled: true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // API call to fetch available tools
  const { data: toolsData, isLoading: isToolsLoading } = useQuery({
    queryKey: ['tools'],
    queryFn: getAgentToolsApi,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
  });

  // Get available tools from the API response
  const availableTools: UltravoxToolData[] =
    toolsData?.data?.data?.results || [];

  // Filter out already selected tools for the dropdown
  const availableToolsForSelection = availableTools.filter(
    (tool: UltravoxToolData) => !formData.tools?.includes(tool.toolId || '')
  );

  // Filter voices based on selected filters
  useEffect(() => {
    if (voiceData) {
      const voices = voiceData.data.data;
      let filtered = [...voices];

      if (selectedLanguage !== 'all') {
        filtered = filtered.filter(
          (voice: Voice) => voice?.language === selectedLanguage
        );
      }
      if (selectedGender !== 'all') {
        filtered = filtered.filter(
          (voice: Voice) => voice.labels?.gender === selectedGender
        );
      }
      if (selectedAccent !== 'all') {
        filtered = filtered.filter(
          (voice: Voice) => voice.labels?.accent === selectedAccent
        );
      }

      setFilteredVoices(filtered);
    }
  }, [voiceData, selectedLanguage, selectedGender, selectedAccent]);

  // Get unique values for dropdowns
  const getUniqueValues = (field: string): string[] => {
    if (!voiceData?.data?.data) return [];
    const values = voiceData.data.data.map(
      (voice: Voice) => voice.labels?.[field]
    );
    return ['all', ...new Set(values.filter(Boolean))] as string[];
  };

  // Get unique languages
  const getUniqueLanguages = (): string[] => {
    if (!voiceData?.data?.data) return [];
    const languages = voiceData.data.data.map(
      (voice: Voice) => voice?.language
    );
    return ['all', ...new Set(languages.filter(Boolean))] as string[];
  };

  const handleSave = () => {
    if (!readOnly) {
      onUpdate(node?.id, formData);
    }
    onClose();
  };

  const handleDelete = () => {
    if (!readOnly) {
      setShowDeleteModal(true);
    }
  };

  const handleConfirmDelete = () => {
    onDelete(node?.id);
    setShowDeleteModal(false);
    onClose(); // Close the config panel after deletion
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const hasUnsavedChanges = (): boolean => {
    if (readOnly || !originalFormData) return false;
    if (formData.stageName !== originalFormData.stageName) return true;
    if (formData.systemPrompt !== originalFormData.systemPrompt) return true;
    if (formData.voice !== originalFormData.voice) return true;
    if (formData.temperature !== originalFormData.temperature) return true;
    if (formData.languageHint !== originalFormData.languageHint) return true;
    if (formData.initialMessage !== originalFormData.initialMessage)
      return true;
    const currentTools = formData.tools || [];
    const originalTools = originalFormData.tools || [];
    if (currentTools.length !== originalTools.length) return true;
    if (currentTools.some((t, i) => t !== originalTools[i])) return true;
    return false;
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setIsDiscardModalOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscardChanges = () => {
    setIsDiscardModalOpen(false);
    onClose();
  };

  const handleKeepEditing = () => {
    setIsDiscardModalOpen(false);
  };

  // Handle tool selection from dropdown
  const handleToolSelect = (toolId: string) => {
    if (!readOnly && toolId && !formData.tools?.includes(toolId)) {
      setFormData(prev => ({
        ...prev,
        tools: [...(prev.tools || []), toolId],
      }));
    }
  };

  // Handle tool removal
  const handleToolRemove = (toolId: string) => {
    if (!readOnly) {
      setFormData(prev => ({
        ...prev,
        tools: prev.tools?.filter(tool => tool !== toolId) || [],
      }));
    }
  };

  const handlePlayPause = (
    voiceId: string,
    previewUrl: string,
    event: React.MouseEvent
  ) => {
    event.stopPropagation(); // Prevent dropdown from closing

    if (currentlyPlayingVoice === voiceId) {
      // Pause current audio
      if (audioElement) {
        audioElement.pause();
        setCurrentlyPlayingVoice(null);
      }
    } else {
      // Stop any currently playing audio
      if (audioElement) {
        audioElement.pause();
      }

      // Play new audio
      const audio = new Audio(previewUrl);
      audio.play();
      setCurrentlyPlayingVoice(voiceId);
      setAudioElement(audio);

      // Handle audio end
      audio.onended = () => {
        setCurrentlyPlayingVoice(null);
        setAudioElement(null);
      };
    }
  };

  const commonSelectStyles = {
    '& .MuiSelect-select': {
      fontSize: '14px',
      fontWeight: 400,
      background: '#F8F9FC',
      display: 'flex',
      padding: '8px 12px',
      alignItems: 'center',
      borderRadius: '6px',
      border: '1px solid #DCE0E7',
      lineHeight: 'normal',
      letterSpacing: '-0.14px',
      color: theme.palette.lightGray.text,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };

  const hasSystemPromptValue =
    formData.systemPrompt && formData.systemPrompt.trim() !== '';

  // Copy to clipboard functionality
  const { copied, copy } = useCopyToClipboard();

  const handleCopySystemPrompt = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent collapse/expand
    copy(formData.systemPrompt || '');
  };

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: '637px',
            p: 0,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#FFFFFF',
          },
        },
      }}
    >
      {/* Sticky Header Section */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#FFFFFF',
          px: 2,
          pt: 2,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {/* Close Button and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                '&:hover': { backgroundColor: '#E8EBF1' },
                transition: 'all 0.2s ease',
              }}
            >
              <XIcon size={18} />
            </IconButton>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 18,
                color: 'custom.textBlue',
                letterSpacing: '-0.2px',
              }}
            >
              {readOnly ? 'View Stage' : 'Configure Stage'}
            </Typography>
          </Box>

          {/* Action Buttons - Only show when not read-only */}
          {!readOnly && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Delete Button */}
              <Button
                size="small"
                variant="outlined"
                onClick={handleDelete}
                startIcon={<TrashIcon size={16} />}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: '6px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  transition: 'all 0.3s ease',
                  color: theme.palette.error.main,
                  borderColor: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: theme.palette.error.main,
                    color: 'white',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                Delete
              </Button>

              {/* Save Button */}
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleSave}
                startIcon={<CheckIcon size={16} />}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: '6px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                Save Changes
              </Button>
            </Box>
          )}
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.08)',
            mb: 1,
            mx: -2,
          }}
        />
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          overflow: 'auto',
          flexGrow: 1,
          px: 2,
          pb: 2,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          {/* Stage Name */}
          <FormControl fullWidth size="small">
            <FormLabel sx={commonStyles.formLabel}>Stage Name</FormLabel>
            <TextField
              value={formData.stageName}
              onChange={e =>
                !readOnly &&
                setFormData(prev => ({ ...prev, stageName: e.target.value }))
              }
              fullWidth
              variant="outlined"
              placeholder="Enter stage name"
              sx={commonStyles.textField}
              disabled={readOnly}
              slotProps={{
                input: {
                  readOnly: readOnly,
                },
              }}
            />
          </FormControl>

          {/* Initial Message - Only show for parent stages without incoming connections */}
          {!hasIncomingConnections && (
            <FormControl fullWidth size="small">
              <FormLabel sx={commonStyles.formLabel}>Initial Message</FormLabel>
              <TextField
                value={formData.initialMessage}
                onChange={e =>
                  !readOnly &&
                  setFormData(prev => ({
                    ...prev,
                    initialMessage: e.target.value,
                  }))
                }
                fullWidth
                variant="outlined"
                placeholder="Enter initial message for this stage"
                sx={commonStyles.textField}
                disabled={readOnly}
                slotProps={{
                  input: {
                    readOnly: readOnly,
                  },
                }}
              />
            </FormControl>
          )}

          {/* System Prompt - Collapsible in both read-only and edit modes */}
          <FormControl fullWidth>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '8px 4px',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
              onClick={() =>
                setIsSystemPromptCollapsed(!isSystemPromptCollapsed)
              }
            >
              <IconButton
                size="small"
                sx={{
                  padding: '2px',
                  marginRight: '8px',
                  color: '#666',
                }}
              >
                {isSystemPromptCollapsed ? (
                  <CaretRightIcon size={16} />
                ) : (
                  <CaretDownIcon size={16} />
                )}
              </IconButton>

              <Typography
                sx={{
                  ...commonStyles.formLabel,
                  margin: 0,
                  flexGrow: 1,
                  fontWeight: hasSystemPromptValue ? 600 : 400,
                  color: hasSystemPromptValue ? '#1976d2' : 'inherit',
                }}
              >
                System Prompt
              </Typography>

              {/* Copy Button */}
              {hasSystemPromptValue && (
                <Tooltip title={copied ? 'Copied!' : 'Copy System Prompt'}>
                  <IconButton
                    size="small"
                    onClick={handleCopySystemPrompt}
                    sx={{
                      padding: '4px',
                      marginRight: '8px',
                      color: copied ? theme.palette.success.main : '#666',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        color: theme.palette.primary.main,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <CopyIcon size={16} />
                  </IconButton>
                </Tooltip>
              )}

              {hasSystemPromptValue && isSystemPromptCollapsed && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontStyle: 'italic',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formData.systemPrompt}
                </Typography>
              )}
            </Box>

            <Collapse
              in={!isSystemPromptCollapsed}
              timeout="auto"
              unmountOnExit
            >
              {readOnly ? (
                // Read-only mode: Use MDEditor in preview mode with dynamic height
                <Box
                  sx={{
                    mt: 1,
                    border: '1px solid #DCE0E7',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    backgroundColor: '#FFFFFF',
                    minHeight: '100px',
                    height: '300px',
                    overflow: 'hidden', // Add overflow handling
                    '& .w-md-editor': {
                      backgroundColor: 'transparent !important',
                      height: '100% !important', // Make MDEditor fill container
                    },
                  }}
                >
                  <MDEditor
                    value={formData.systemPrompt || 'No system prompt defined'}
                    preview="preview"
                    hideToolbar={true}
                    visibleDragbar={false}
                    data-color-mode="light"
                    style={{
                      backgroundColor: 'transparent',
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '14px',
                      height: '100%', // Change from 'auto' to '100%'
                    }}
                  />
                </Box>
              ) : (
                <TextField
                  value={formData.systemPrompt}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      systemPrompt: e.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={1}
                  variant="outlined"
                  placeholder="Enter system prompt for this stage"
                  sx={{
                    marginTop: '8px',
                    marginBottom: '8px',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      borderRadius: '6px',
                      '& fieldset': {
                        borderColor: '#E0E0E0',
                      },
                    },
                  }}
                />
              )}
            </Collapse>
          </FormControl>

          {/* Voice Configuration Section */}
          <Box>
            <FormLabel sx={commonStyles.formLabel}>
              Voice Configuration (Ultra TTS)
            </FormLabel>

            {/* Voice Filters Grid - Disabled in read-only mode */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 2,
                mt: 1,
                mb: 2,
                p: 2,
                borderRadius: '8px',
                border: '1px solid #DCE0E7',
                backgroundColor: '#FFFFFF',
              }}
            >
              {/* Language Filter */}
              <FormControl size="small">
                <FormLabel
                  sx={{ ...commonStyles.formLabel, fontSize: '12px', mb: 0.5 }}
                >
                  Language
                </FormLabel>
                <Select
                  value={selectedLanguage}
                  onChange={e =>
                    !readOnly && setSelectedLanguage(e.target.value)
                  }
                  sx={commonSelectStyles}
                  disabled={readOnly || isLoadingVoices}
                >
                  {getUniqueLanguages().map(lang => (
                    <MenuItem key={lang} value={lang}>
                      {lang === 'all'
                        ? 'All Languages'
                        : lang.charAt(0).toUpperCase() + lang.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Gender Filter */}
              <FormControl size="small">
                <FormLabel
                  sx={{ ...commonStyles.formLabel, fontSize: '12px', mb: 0.5 }}
                >
                  Gender
                </FormLabel>
                <Select
                  value={selectedGender}
                  onChange={e => !readOnly && setSelectedGender(e.target.value)}
                  sx={commonSelectStyles}
                  disabled={readOnly || isLoadingVoices}
                >
                  {getUniqueValues('gender').map(gender => (
                    <MenuItem key={gender} value={gender}>
                      {gender === 'all'
                        ? 'All Genders'
                        : gender.charAt(0).toUpperCase() + gender.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Accent Filter */}
              <FormControl size="small">
                <FormLabel
                  sx={{ ...commonStyles.formLabel, fontSize: '12px', mb: 0.5 }}
                >
                  Accent
                </FormLabel>
                <Select
                  value={selectedAccent}
                  onChange={e => !readOnly && setSelectedAccent(e.target.value)}
                  sx={commonSelectStyles}
                  disabled={readOnly || isLoadingVoices}
                >
                  {getUniqueValues('accent').map(accent => (
                    <MenuItem key={accent} value={accent}>
                      {accent === 'all'
                        ? 'All Accents'
                        : accent.charAt(0).toUpperCase() + accent.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Voice Selection Dropdown */}
            <FormControl fullWidth size="small">
              <FormLabel sx={{ ...commonStyles.formLabel, mb: 1 }}>
                Selected Voice
              </FormLabel>
              <Select
                value={
                  typeof formData.voice === 'string'
                    ? formData.voice
                    : formData.voice?.value || 'default'
                }
                onChange={e => {
                  if (readOnly) return;
                  const selectedVoiceId = e.target.value;
                  if (selectedVoiceId === 'default') {
                    setFormData(prev => ({ ...prev, voice: 'default' }));
                  } else {
                    // Find the selected voice to get both name and value
                    const selectedVoice = filteredVoices.find(
                      voice => voice.voice_id === selectedVoiceId
                    );
                    if (selectedVoice) {
                      setFormData(prev => ({
                        ...prev,
                        voice: {
                          name: selectedVoice.name,
                          value: selectedVoice.voice_id,
                        },
                      }));
                    }
                  }
                }}
                sx={commonSelectStyles}
                disabled={
                  readOnly || isLoadingVoices || filteredVoices.length === 0
                }
                displayEmpty
              >
                <MenuItem value="default">
                  {isLoadingVoices ? 'Loading voices...' : 'Default Voice'}
                </MenuItem>
                {filteredVoices.map(voice => (
                  <MenuItem key={voice._id} value={voice.voice_id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {voice.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary' }}
                        >
                          {voice.language} • {voice.labels?.gender || 'Unknown'}{' '}
                          • {voice.labels?.accent || 'No accent'}
                        </Typography>
                      </Box>

                      {voice.preview_url && (
                        <IconButton
                          size="small"
                          onClick={e =>
                            handlePlayPause(voice._id, voice.preview_url, e)
                          }
                          sx={{
                            ml: 1,
                            color: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: 'rgba(90, 76, 230, 0.1)',
                            },
                          }}
                        >
                          {currentlyPlayingVoice === voice._id ? (
                            <PauseIcon size={16} />
                          ) : (
                            <PlayIcon size={16} />
                          )}
                        </IconButton>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Temperature - Read-only in read-only mode */}
          <Box>
            <FormLabel sx={commonStyles.formLabel}>
              Temperature ({formData.temperature})
            </FormLabel>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 1,
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: '12px',
                  color: theme.palette.text.secondary,
                }}
              >
                Focused
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '12px',
                  color: theme.palette.text.secondary,
                }}
              >
                Creative
              </Typography>
            </Box>
            <Slider
              value={formData.temperature}
              onChange={(_, value) =>
                !readOnly &&
                setFormData(prev => ({ ...prev, temperature: value as number }))
              }
              min={0}
              max={1}
              step={0.1}
              valueLabelDisplay="auto"
              getAriaValueText={value => value.toFixed(1)}
              valueLabelFormat={value => value.toFixed(1)}
              disabled={readOnly}
              sx={{
                color: 'primary.main',
                '& .MuiSlider-thumb': {
                  width: 16,
                  height: 16,
                  backgroundColor: '#fff',
                  border: '2px solid currentColor',
                },
                '& .MuiSlider-track': {
                  height: 2,
                },
                '& .MuiSlider-rail': {
                  height: 2,
                  opacity: 0.5,
                  backgroundColor: '#E5E7EB',
                },
              }}
            />
          </Box>

          {/* Language Hint */}
          <FormControl fullWidth size="small">
            <FormLabel sx={commonStyles.formLabel}>Language Hint</FormLabel>
            <TextField
              value={formData.languageHint}
              onChange={e =>
                !readOnly &&
                setFormData(prev => ({ ...prev, languageHint: e.target.value }))
              }
              fullWidth
              variant="outlined"
              placeholder="e.g., en, es, fr"
              sx={commonStyles.textField}
              disabled={readOnly}
              InputProps={{
                readOnly: readOnly,
              }}
            />
          </FormControl>

          {/* Tools - Dropdown selection similar to Available Brands */}
          <Box>
            <FormLabel sx={commonStyles.formLabel}>Available Tools</FormLabel>

            {/* Tool Selection Area */}
            {!readOnly && (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
              >
                {/* Selected Tools Display - Full Width */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    width: '100%',
                    minHeight: '60px',
                    bgcolor: 'white',
                    p: 2,
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                  }}
                >
                  {formData.tools && formData.tools.length > 0 ? (
                    formData.tools.map((toolId, index) => {
                      const toolData = availableTools.find(
                        (tool: UltravoxToolData) => tool.toolId === toolId
                      );
                      return (
                        <Chip
                          key={index}
                          label={toolData?.name || toolId}
                          onDelete={() => handleToolRemove(toolId)}
                          sx={{
                            height: '32px',
                            fontSize: '14px',
                            '& .MuiChip-label': {
                              px: 1.5,
                            },
                          }}
                        />
                      );
                    })
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '32px',
                        width: '100%',
                        justifyContent: 'center',
                      }}
                    >
                      No tools selected
                    </Typography>
                  )}
                </Box>

                {/* Tool Selection Dropdown - Full Width */}
                <Select
                  variant="outlined"
                  size="small"
                  onChange={e => handleToolSelect(e.target.value)}
                  value=""
                  displayEmpty
                  renderValue={() => 'Select tool'}
                  fullWidth
                  sx={{
                    height: '48px',
                    borderRadius: '6px',
                    textTransform: 'none',
                    color: 'text.secondary',
                    borderColor: 'lightGray.main',
                    fontSize: '14px',
                    bgcolor: '#F8F9FC',
                    '& .MuiSelect-select': {
                      py: 1.5,
                    },
                  }}
                >
                  {availableToolsForSelection.length === 0 ? (
                    <MenuItem disabled>
                      {isToolsLoading
                        ? 'Loading tools...'
                        : 'No tools available'}
                    </MenuItem>
                  ) : (
                    availableToolsForSelection.map((tool: UltravoxToolData) => (
                      <MenuItem key={tool.toolId} value={tool.toolId}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {tool.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                          >
                            {tool.definition.description}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', fontSize: '10px' }}
                          >
                            {tool.ownership === 'public'
                              ? 'Built-in'
                              : 'Custom'}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </Box>
            )}

            {/* Read-only display of selected tools - Full Width */}
            {readOnly && formData.tools && formData.tools.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  mb: 2,
                  mt: 1,
                  p: 2,
                  borderRadius: '8px',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  bgcolor: '#f9f9f9',
                  minHeight: '60px',
                  alignItems: 'center',
                }}
              >
                {formData.tools.map((toolId, index) => {
                  const toolData = availableTools.find(
                    (tool: UltravoxToolData) => tool.toolId === toolId
                  );
                  return (
                    <Chip
                      key={index}
                      label={toolData?.name || toolId}
                      sx={{
                        backgroundColor: theme.palette.custom.sidebarIconBg,
                        color: theme.palette.primary.main,
                        fontSize: '14px',
                        height: '32px',
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  );
                })}
              </Box>
            )}

            {/* Show empty state when no tools are selected */}
            {(!formData.tools || formData.tools.length === 0) && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  mt: 1,
                  p: 3,
                  textAlign: 'center',
                  border: '1px dashed rgba(0, 0, 0, 0.23)',
                  borderRadius: '8px',
                  bgcolor: '#f9f9f9',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                No tools selected for this stage
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Custom styles for MDEditor */}
      <style>{`
        .w-md-editor {
          border: none !important;
          box-shadow: none !important;
          font-family: 'DM Sans', sans-serif !important;
          font-size: 14px !important;
          background-color: transparent !important;
          height: 100% !important; /* Ensure MDEditor fills container */
        }
        .w-md-editor-text-input,
        .w-md-editor-text-container,
        .w-md-editor-text,
        .w-md-editor-preview,
        .wmde-markdown {
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
          font-size: 14px !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          background-color: transparent !important;
          min-height: auto !important;
        }
        .w-md-editor-preview,
        .wmde-markdown {
          overflow-y: auto !important; /* Add scrolling when content exceeds container */
          height: 100% !important; /* Change from 'auto' to '100%' */
          max-height: 100% !important;
        }
        .w-md-editor-preview h3,
        .wmde-markdown h3 {
          font-weight: 600 !important;
          color: #374151 !important;
          margin: 0.8em 0 0.4em 0 !important;
          font-size: 16px !important;
        }
        .w-md-editor-preview h3 .field-name,
        .wmde-markdown h3 .field-name {
          color: #1976d2 !important;
        }
        .w-md-editor-preview h1,
        .w-md-editor-preview h2,
        .wmde-markdown h1,
        .wmde-markdown h2 {
          font-weight: 500 !important;
          color: #6B7280 !important;
          margin: 0.6em 0 0.3em 0 !important;
          font-size: 15px !important;
        }
        .w-md-editor-preview p,
        .wmde-markdown p {
          margin: 0.5em 0 !important;
          line-height: 1.5 !important;
        }
        .w-md-editor-preview:last-child p:last-child,
        .wmde-markdown:last-child p:last-child {
          margin-bottom: 0 !important;
        }
        .w-md-editor-preview > *:first-child,
        .wmde-markdown > *:first-child {
          margin-top: 0 !important;
        }
      `}</style>

      {/* Add the ConfirmationModal before the closing Drawer tag */}
      <ConfirmationModal
        open={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isPending={false}
        title="Delete Stage"
        description={`Are you sure you want to delete the stage "${node?.data?.stageName}"? This action cannot be undone.`}
        confirmButtonText="Delete Stage"
        cancelButtonText="Cancel"
        icon={TrashIcon}
        confirmButtonColor="error"
      />
      <ConfirmationModal
        open={isDiscardModalOpen}
        onClose={handleKeepEditing}
        onConfirm={handleDiscardChanges}
        title="Discard Changes?"
        description="You have unsaved changes. Are you sure you want to discard them?"
        confirmButtonText="Discard"
        cancelButtonText="Keep Editing"
        isPending={false}
      />
    </Drawer>
  );
};

export default StageConfigPanel;
